// ========================================
// pages/Upgrade.js - User Upgrade Page
// ========================================
// PROJECT: Agyapadie Vault - Digital Inheritance Platform
// LAST MODIFIED: April 12, 2026
// 
// PURPOSE: Allow users to upgrade their plan to a higher tier
// 
// UPGRADE METHODS: ONLY PIN CODE (voucher purchased from authorized agents)
// - Pay online via Paystack (coming soon)
// 
// PRICING MODEL: ONE-TIME PAYMENT (Lifetime Access)
// - User pays once, keeps plan forever
// - NO recurring subscriptions or renewal fees
// - PINs are valid for one year from purchase date
// 
// ASSETS USED:
// - /images/plans/plan-free.png
// - /images/plans/plan-premium.png
// - /images/plans/plan-business.png
// - /images/plans/plan-enterprise.png
// - /images/plans/popular-tag.png (optional)
// 
// RESPONSIVE BREAKPOINTS:
// - Desktop (>1024px): 4 plan cards in a row
// - Tablet (768px-1024px): 2 plan cards per row
// - Mobile (<768px): 1 plan card per row
// 
// FOOTER: Included (full width, proper spacing)
// ========================================

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import Footer from '../components/Footer';

const Upgrade = () => {
  const navigate = useNavigate();
  
  // ========================================
  // STATE VARIABLES
  // ========================================
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pinCode, setPinCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [checkingPin, setCheckingPin] = useState(false);
  const [pinValid, setPinValid] = useState(null);
  const [pinPlan, setPinPlan] = useState(null);

  // ========================================
  // FETCH USER DATA ON COMPONENT MOUNT
  // ========================================
  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // CHECK PIN VALIDITY (Without Redeeming)
  // ========================================
  const handleCheckPin = async () => {
    if (!pinCode.trim()) {
      setError('Please enter a PIN code');
      return;
    }

    setCheckingPin(true);
    setError('');
    setPinValid(null);
    setPinPlan(null);

    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/redeem/check/${pinCode}`);
      
      if (response.data.valid) {
        setPinValid(true);
        setPinPlan(response.data.plan);
      } else {
        setPinValid(false);
        const messages = {
          not_found: 'Invalid PIN code',
          already_used: 'This PIN has already been used',
          expired: 'This PIN has expired (valid for 1 year from purchase)'
        };
        setError(messages[response.data.reason] || 'Invalid PIN');
      }
    } catch (err) {
      setError('Failed to verify PIN');
    } finally {
      setCheckingPin(false);
    }
  };

  // ========================================
  // REDEEM PIN CODE - UPGRADE USER PLAN
  // ========================================
  const handleRedeem = async () => {
    if (!pinCode.trim()) {
      setError('Please enter a PIN code');
      return;
    }

    setRedeeming(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/redeem/pin`, 
        { pinCode: pinCode },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setSuccess(response.data.message);
      
      // Refresh user data to show new plan limits
      const userResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      localStorage.setItem('user', JSON.stringify(userResponse.data.user));
      setUser(userResponse.data.user);
      
      setPinCode('');
      setPinValid(null);
      setPinPlan(null);
      
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to redeem PIN');
    } finally {
      setRedeeming(false);
    }
  };

  // ========================================
  // PLAN PRIORITY MAPPING
  // ========================================
  const planPriority = { 
    free: 0,
    premium: 1,
    business: 2,
    enterprise: 3
  };
  
  const currentPriority = user ? planPriority[user.plan] : 0;
  const canUpgradeTo = (plan) => planPriority[plan] > currentPriority;

  // ========================================
  // PLAN DATA FOR DISPLAY
  // ========================================
  const plans = [
    {
      name: 'Free',
      price: 'GHS 0',
      period: 'free',
      icon: '/images/plans/plan-free.png',
      features: [
        '2 Global Next of Kin',
        'Up to 10 Assets',
        '2 Trustees',
        'No External Beneficiaries',
        '2MB file uploads',
        'Basic verification (1 choice)'
      ],
      isCurrent: user?.plan === 'free',
      canUpgrade: false,
      buttonText: 'Current Plan',
      buttonLink: '#'
    },
    {
      name: 'Premium',
      price: 'GHS 399',
      period: 'one-time',
      icon: '/images/plans/plan-premium.png',
      features: [
        '4 Global Next of Kin',
        'Up to 50 Assets',
        'Up to 3 Trustees',
        '1 External Beneficiary PER ASSET',
        '5MB file uploads',
        'Choose 1 or 2 of 3 Verifiers'
      ],
      isCurrent: user?.plan === 'premium',
      canUpgrade: canUpgradeTo('premium'),
      buttonText: user?.plan === 'premium' ? 'Current Plan' : 'Upgrade',
      buttonLink: '#',
      popular: true
    },
    {
      name: 'Business',
      price: 'GHS 999',
      period: 'one-time',
      icon: '/images/plans/plan-business.png',
      features: [
        '6 Global Next of Kin',
        'Up to 100 Assets',
        'Up to 6 Trustees',
        '2 External Beneficiaries PER ASSET',
        '10MB file uploads',
        'Choose 1, 2, or 3 of 6 Verifiers',
        'Priority email support'
      ],
      isCurrent: user?.plan === 'business',
      canUpgrade: canUpgradeTo('business'),
      buttonText: user?.plan === 'business' ? 'Current Plan' : 'Upgrade',
      buttonLink: '#'
    },
    {
      name: 'Enterprise',
      price: 'GHS 2,999',
      period: 'one-time',
      icon: '/images/plans/plan-enterprise.png',
      features: [
        '10 Global Next of Kin',
        'Up to 400 Assets',
        'Up to 10 Trustees',
        '3 External Beneficiaries PER ASSET',
        '20MB file uploads',
        'Choose 1-4 of 10 Verifiers',
        'Priority 24/7 support',
        'Custom integration options'
      ],
      isCurrent: user?.plan === 'enterprise',
      canUpgrade: canUpgradeTo('enterprise'),
      buttonText: user?.plan === 'enterprise' ? 'Current Plan' : (user?.plan === 'business' ? 'Upgrade' : 'Contact Sales'),
      buttonLink: user?.plan === 'enterprise' ? '#' : (user?.plan === 'business' ? '#' : '/contact')
    }
  ];

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F5F0E6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F5F0E6', display: 'flex', flexDirection: 'column' }}>
      
      {/* MAIN CONTENT */}
      <div style={{ padding: '20px', flex: 1 }}>
        
        {/* HEADER */}
        <div className="upgrade-header">
          <Link to="/dashboard" className="back-link">← Back to Dashboard</Link>
          <h1 className="upgrade-title">Upgrade Your Plan</h1>
          <div className="upgrade-placeholder"></div>
        </div>

        <div className="upgrade-container">
          
          {/* CURRENT PLAN DISPLAY */}
          <div className="current-plan-card">
            <h2 className="current-plan-title">Current Plan</h2>
            <div className="current-plan-badge">{user?.plan?.toUpperCase()}</div>
            <p className="current-plan-desc">
              {user?.plan === 'free' && 'You are on the Free plan. Upgrade to add external beneficiaries to your assets and unlock higher limits.'}
              {user?.plan === 'premium' && 'You are on the Premium plan. Add external beneficiaries to your assets. Upgrade to Business or Enterprise for more.'}
              {user?.plan === 'business' && 'You are on the Business plan. Upgrade to Enterprise for maximum limits.'}
              {user?.plan === 'enterprise' && '🎉 You are on the Enterprise plan! All features unlocked.'}
            </p>
          </div>

          {/* PIN REDEMPTION SECTION */}
          <div className="pin-card">
            <h2 className="pin-title">🔑 Have a Voucher PIN?</h2>
            <p className="pin-subtitle">
              Enter your 16-character PIN code to upgrade your plan instantly.
              PINs provide <strong>LIFETIME access</strong> to your new plan.
            </p>
            
            <div className="pin-input-group">
              <input
                type="text"
                placeholder="e.g., AGY-PREM-A3F9-7B2D"
                value={pinCode}
                onChange={(e) => {
                  setPinCode(e.target.value.toUpperCase());
                  setPinValid(null);
                  setError('');
                }}
                className="pin-input"
              />
              <button
                onClick={handleCheckPin}
                disabled={checkingPin || !pinCode}
                className="check-btn"
              >
                {checkingPin ? 'Checking...' : 'Check PIN'}
              </button>
            </div>
            
            {pinValid === true && pinPlan && (
              <div className="pin-valid-box">
                <span>✅ Valid {pinPlan.toUpperCase()} PIN - Ready to upgrade!</span>
                {canUpgradeTo(pinPlan) ? (
                  <button onClick={handleRedeem} disabled={redeeming} className="redeem-btn">
                    {redeeming ? 'Processing...' : `Upgrade to ${pinPlan.toUpperCase()} (Lifetime)`}
                  </button>
                ) : (
                  <div className="cannot-upgrade-box">
                    ⚠️ You are already on a higher or equal plan.
                  </div>
                )}
              </div>
            )}
            
            {error && <div className="error-box">{error}</div>}
            {success && <div className="success-box">{success}</div>}
          </div>

          {/* PLANS GRID */}
          <div className="plans-grid">
            {plans.map((plan, idx) => (
              <div 
                key={idx} 
                className={`plan-card ${plan.popular ? 'popular' : ''} ${plan.isCurrent ? 'current-plan-border' : ''}`}
              >
                {plan.popular && <div className="popular-tag">🏆 BEST VALUE</div>}
                <img src={plan.icon} alt={plan.name} className="plan-icon-img" />
                <h3 className="plan-name">{plan.name}</h3>
                <div className="plan-price">
                  <span className="price-amount">{plan.price}</span>
                  <span className="price-period">{plan.period}</span>
                </div>
                <div className="lifetime-badge">🔒 Lifetime Access</div>
                <ul className="plan-features">
                  {plan.features.map((feature, fIdx) => (
                    <li key={fIdx}>
                      {feature.includes('No External') ? '✗' : '✓'} {feature}
                    </li>
                  ))}
                </ul>
                {plan.isCurrent ? (
                  <div className="current-tag">✓ CURRENT PLAN</div>
                ) : plan.canUpgrade ? (
                  <div className="upgrade-note">💡 Purchase a PIN to upgrade</div>
                ) : (
                  <div className="cannot-upgrade-tag">
                    {user?.plan === 'enterprise' ? 'Maximum plan reached' : 'Upgrade to higher plan first'}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* BENEFICIARY LOGIC EXPLANATION */}
          <div className="beneficiary-info-box">
            <h4 className="info-title">📖 Understanding Beneficiaries in Agyapadie Vault</h4>
            
            <div className="info-grid">
              <div className="info-column">
                <strong className="nok-title">🌍 Global Next of Kin (NOKs)</strong>
                <ul className="info-list">
                  <li>✅ <strong>Account-level beneficiaries</strong> - Created once, used everywhere</li>
                  <li>🔗 Automatically linked to ALL assets by DEFAULT</li>
                  <li>✏️ Can be unlinked per asset (if replaced with externals)</li>
                  <li>🛡️ <strong>Cannot delete last NOK</strong> - System prevents it</li>
                  <li>📌 Minimum 1 required before creating any asset</li>
                </ul>
              </div>
              <div className="info-column">
                <strong className="external-title">👤 External Beneficiaries</strong>
                <ul className="info-list">
                  <li>➕ <strong>Asset-level beneficiaries</strong> - Created per asset</li>
                  <li>📊 Plan limit = Maximum number <strong>PER SINGLE ASSET</strong></li>
                  <li>🚫 Free plan: 0 external beneficiaries allowed</li>
                  <li>🗑️ Can be deleted entirely (no restrictions)</li>
                  <li>💡 Created during asset creation OR from External page</li>
                </ul>
              </div>
            </div>
            
            <div className="critical-rule-box">
              <strong>⚠️ CRITICAL RULE - Asset Creation:</strong>
              <p className="critical-rule-text">
                Every asset must have AT LEAST ONE beneficiary (NOK or External). 
                If you uncheck all NOKs and add no external beneficiaries, asset creation will be <strong>REJECTED</strong>.
              </p>
            </div>
            
            <div className="info-examples">
              <p className="info-example-title">📋 Valid Asset Beneficiary Configurations:</p>
              <ul className="info-list">
                <li><strong>✅ All Global NOKs only</strong> - Default configuration</li>
                <li><strong>✅ Selected Global NOKs only</strong> - Choose specific NOKs</li>
                <li><strong>✅ External beneficiaries only</strong> - Uncheck all NOKs, add externals</li>
                <li><strong>✅ Mixed</strong> - Selected NOKs + External beneficiaries</li>
                <li><strong style={{color: '#dc2626'}}>❌ No beneficiaries</strong> - System REJECTS creation</li>
              </ul>
            </div>
            
            <div className="warning-box">
              💡 <strong>Pro Tip:</strong> Global NOKs serve as your safety net. 
              Even if you delete all external beneficiaries from an asset, your asset still has beneficiaries (the NOKs).
            </div>
          </div>

          {/* INFORMATION NOTE ABOUT PINs */}
          <div className="note-box">
            <p>💡 <strong>How to get a PIN?</strong></p>
            <p>Purchase a voucher PIN from authorized agents, mobile money vendors (MTN MoMo, Vodafone Cash), or online resellers.</p>
            <p>All plans are <strong>ONE-TIME PAYMENT</strong> - pay once, use forever. No recurring subscriptions.</p>
          </div>
          
        </div>
      </div>

      {/* FOOTER */}
      <Footer />

      <style dangerouslySetInnerHTML={{ __html: `
        
        .upgrade-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          max-width: 1200px;
          margin: 0 auto 30px;
          width: 100%;
          flex-wrap: wrap;
          gap: 15px;
        }
        
        .back-link {
          color: #2E7D32;
          text-decoration: none;
          font-size: 14px;
          transition: color 0.3s;
        }
        
        .back-link:hover {
          color: #FFD700;
        }
        
        .upgrade-title {
          font-size: 28px;
          color: #1A1A1A;
          margin: 0;
        }
        
        .upgrade-placeholder {
          width: 100px;
        }
        
        .upgrade-container {
          max-width: 1200px;
          margin: 0 auto 30px;
          width: 100%;
        }
        
        /* Current Plan Card */
        .current-plan-card {
          background: white;
          border-radius: 12px;
          padding: 25px;
          margin-bottom: 30px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        
        .current-plan-title {
          font-size: 16px;
          color: #666;
          margin-bottom: 10px;
        }
        
        .current-plan-badge {
          display: inline-block;
          padding: 8px 24px;
          background: #FFD700;
          color: #1A1A1A;
          border-radius: 30px;
          font-weight: bold;
          font-size: 16px;
          margin-bottom: 10px;
        }
        
        .current-plan-desc {
          color: #666;
          font-size: 14px;
          max-width: 600px;
          margin: 0 auto;
        }
        
        /* PIN Card */
        .pin-card {
          background: white;
          border-radius: 12px;
          padding: 30px;
          margin-bottom: 30px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        
        .pin-title {
          font-size: 20px;
          color: #FFD700;
          margin-bottom: 10px;
        }
        
        .pin-subtitle {
          color: #666;
          margin-bottom: 20px;
        }
        
        .pin-input-group {
          display: flex;
          gap: 12px;
          max-width: 450px;
          margin: 0 auto;
        }
        
        .pin-input {
          flex: 1;
          padding: 12px 16px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          font-family: monospace;
          letter-spacing: 1px;
        }
        
        .pin-input:focus {
          outline: none;
          border-color: #FFD700;
        }
        
        .check-btn {
          padding: 12px 24px;
          background: #2E7D32;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .check-btn:hover {
          background: #1B5E20;
        }
        
        .pin-valid-box {
          margin-top: 20px;
          padding: 15px;
          background: #d1fae5;
          border-radius: 8px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }
        
        .redeem-btn {
          padding: 10px 24px;
          background: #FFD700;
          color: #1A1A1A;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: bold;
        }
        
        .redeem-btn:hover {
          background: #B8860B;
        }
        
        .cannot-upgrade-box {
          margin-top: 20px;
          padding: 15px;
          background: #fee2e2;
          border-radius: 8px;
          color: #dc2626;
          text-align: center;
          width: 100%;
        }
        
        /* Plans Grid */
        .plans-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 25px;
          margin-bottom: 40px;
        }
        
        .plan-card {
          background: white;
          border-radius: 12px;
          padding: 25px 20px;
          text-align: center;
          position: relative;
          transition: all 0.3s;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          display: flex;
          flex-direction: column;
        }
        
        .plan-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.1);
        }
        
        .plan-card.popular {
          border: 2px solid #FFD700;
          transform: scale(1.02);
        }
        
        .plan-card.current-plan-border {
          border: 2px solid #2E7D32;
        }
        
        .popular-tag {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          background: #FFD700;
          color: #1A1A1A;
          padding: 4px 16px;
          border-radius: 30px;
          font-size: 12px;
          font-weight: bold;
          white-space: nowrap;
        }
        
        .plan-icon-img {
          height: 70px;
          width: auto;
          margin: 0 auto 15px;
        }
        
        .plan-name {
          font-size: 20px;
          font-weight: bold;
          margin-bottom: 10px;
        }
        
        .plan-price {
          margin-bottom: 10px;
        }
        
        .price-amount {
          font-size: 28px;
          font-weight: bold;
          color: #FFD700;
        }
        
        .price-period {
          font-size: 11px;
          color: #666;
          display: block;
        }
        
        .lifetime-badge {
          font-size: 11px;
          color: #2E7D32;
          background: #E8F5E9;
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          margin-bottom: 15px;
        }
        
        .plan-features {
          list-style: none;
          padding: 0;
          margin: 0 0 20px 0;
          text-align: left;
          flex: 1;
        }
        
        .plan-features li {
          font-size: 12px;
          padding: 6px 0;
          color: #444;
        }
        
        .current-tag {
          margin-top: 15px;
          padding: 8px;
          background: #2E7D32;
          color: white;
          border-radius: 8px;
          font-size: 12px;
          font-weight: bold;
        }
        
        .upgrade-note {
          margin-top: 15px;
          padding: 8px;
          background: #FFF8E1;
          color: #B8860B;
          border-radius: 8px;
          font-size: 11px;
        }
        
        .cannot-upgrade-tag {
          margin-top: 15px;
          padding: 8px;
          background: #fee2e2;
          color: #dc2626;
          border-radius: 8px;
          font-size: 12px;
        }
        
        /* Beneficiary Info Box */
        .beneficiary-info-box {
          background: #E8F5E9;
          border-radius: 12px;
          padding: 25px;
          margin-bottom: 25px;
          border-left: 4px solid #2E7D32;
        }
        
        .info-title {
          margin: 0 0 15px 0;
          color: #1B5E20;
        }
        
        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 25px;
          margin-bottom: 20px;
        }
        
        .info-list {
          margin: 5px 0 0 20px;
          padding: 0;
        }
        
        .info-list li {
          margin-bottom: 5px;
        }
        
        .critical-rule-box {
          margin-top: 15px;
          padding: 12px;
          background: #FFEBEE;
          border-left: 4px solid #dc2626;
          border-radius: 8px;
          margin-bottom: 15px;
        }
        
        .critical-rule-text {
          margin: 8px 0 0 0;
          font-size: 12px;
          color: #b91c1c;
        }
        
        .info-examples {
          margin-top: 15px;
          padding: 12px;
          background: #F3E8FF;
          border-radius: 8px;
        }
        
        .info-example-title {
          margin: 0 0 8px 0;
          font-weight: bold;
          font-size: 13px;
          color: #6B21A5;
        }
        
        .warning-box {
          margin-top: 15px;
          padding: 12px;
          background: #E0F2FE;
          border-left: 4px solid #0284C7;
          border-radius: 8px;
          font-size: 12px;
          color: #0369A1;
        }
        
        .note-box {
          background: #e8f4fd;
          border-radius: 12px;
          padding: 20px;
          text-align: center;
          color: #1A1A1A;
        }
        
        .error-box {
          margin-top: 15px;
          padding: 12px;
          background: #fee2e2;
          border: 1px solid #ef4444;
          color: #dc2626;
          border-radius: 8px;
        }
        
        .success-box {
          margin-top: 15px;
          padding: 12px;
          background: #d1fae5;
          border: 1px solid #10b981;
          color: #065f46;
          border-radius: 8px;
        }
        
        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #F5F0E6;
          border-top: 4px solid #FFD700;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 1024px) {
          .plans-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
          }
        }
        
        @media (max-width: 768px) {
          .upgrade-header {
            flex-direction: column;
            text-align: center;
            margin-bottom: 20px;
          }
          
          .upgrade-title {
            font-size: 24px;
          }
          
          .upgrade-placeholder {
            display: none;
          }
          
          .plans-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          
          .plan-card.popular {
            transform: scale(1);
          }
          
          .pin-input-group {
            flex-direction: column;
          }
          
          .info-grid {
            grid-template-columns: 1fr;
            gap: 15px;
          }
          
          .pin-valid-box {
            flex-direction: column;
            text-align: center;
          }
        }
        
        @media (max-width: 480px) {
          .upgrade-title {
            font-size: 20px;
          }
          
          .plan-card {
            padding: 20px;
          }
          
          .price-amount {
            font-size: 24px;
          }
          
          .plan-name {
            font-size: 18px;
          }
          
          .plan-icon-img {
            height: 55px;
          }
        }
        
      `}} />
    </div>
  );
};

export default Upgrade;